import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ExternalLink } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
 
import { PageHeader } from '../components/PageHeader';
import { ChartCard } from '../components/ChartCard';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { apiGet, apiPost } from '../../lib/api-client';
 
const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
 
const fmtInrCompact = (n) => {
  const value = toNumber(n);
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
 
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toLocaleString("en-IN", { maximumFractionDigits: 1 })} Cr`;
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toLocaleString("en-IN", { maximumFractionDigits: 1 })} L`;
  return fmt(value);
};
 
const fmtCompact = (n) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
 
export function BenchmarksPage() {
  const account = useSelector((state) => state.account || {});
  const [peers, setPeers] = useState([]);
  const [peersLoading, setPeersLoading] = useState(false);
  const [peersSyncing, setPeersSyncing] = useState(false);
  const [peersError, setPeersError] = useState(null);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsSyncing, setNewsSyncing] = useState(false);
  const [newsError, setNewsError] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const organizationNewsProfile = getOrganizationNewsProfile(account);
  const newsIndustry = organizationNewsProfile.industry;
  const newsCountry = organizationNewsProfile.country;
  const [benchmarkPeers, setBenchmarkPeers] = useState([]);
  const [peerSuggestions, setPeerSuggestions] = useState([]);
  const [suggestingPeers, setSuggestingPeers] = useState(false);
  const [selectedPeerCins, setSelectedPeerCins] = useState({})

  const orgId = useSelector(
    (state) =>
      state?.account?.org?.id ||
      state?.account?.org_data?.data?.id ||
      state?.account?.org_member?.org_id
  );

  useEffect(() => {
    const controller = new AbortController();

    const loadPeerCompanies = async () => {
      try {
        
        const response = await apiGet(
          `/organization/benchmark-peers/${orgId ? `?org_id=${encodeURIComponent(orgId)}` : ""}`
        );

        setBenchmarkPeers(response.rows ?? []);
      } catch (error) {
        setPeersError(error instanceof Error ? error.message : "Unable to load peer companies.");
      }
    };

    loadPeerCompanies();

    return () => controller.abort();
  }, []);
 
  const syncIndustryNews = async () => {
    if (!hasBenchmarkNewsTarget({ industry: newsIndustry, country: newsCountry })) return;
    setNewsSyncing(true);
    setNewsError(null);
    try {
      const response = await apiPost("/user/industry-news/", {
        sectors: [newsIndustry.trim()],
        regions: [newsCountry.trim()],
        industry: newsIndustry.trim(),
        country: newsCountry.trim(),
        limit: 24,
      });
      setNews(sortTimestampedNews(response.items ?? []));
      if (response.enrichment_error) {
        setNewsError(`${response.enrichment}: ${response.enrichment_error}`);
      }
    } catch (error) {
      setNewsError(error instanceof Error ? error.message : "Unable to sync industry news.");
    } finally {
      setNewsSyncing(false);
    }
  };
  const suggestPeers = async () => {

    console.log("Benchmark orgId:", orgId);

    if (!orgId) {
      setPeersError("Organization ID not found in localStorage.");
      return;
    }

    setSuggestingPeers(true);
    setPeersError(null);

    try {
      const response = await apiPost("/organization/benchmarks/suggest-peers/", {
        org_id: orgId,
        industry: newsIndustry || "",
        country: newsCountry || "India",
        products: [],
        revenue_range: "",
      });

      const suggestions = response.suggestions ?? [];
      setPeerSuggestions(suggestions);

      const selected = {};
      suggestions.forEach((item) => {
        if (item.verified && item.company_cin) {
          selected[item.company_cin] = true;
        }
      });

      setSelectedPeerCins(selected);
    } catch (error) {
      setPeersError(error instanceof Error ? error.message : "Unable to suggest peers.");
    } finally {
      setSuggestingPeers(false);
    }
  };
  const saveSelectedPeers = async () => {
    const selectedCompanies = peerSuggestions.filter(
      (item) => item.company_cin && selectedPeerCins[item.company_cin]
    );

    const response = await apiPost("/organization/benchmark-peers/", {
      org_id: orgId,
      companies: selectedCompanies,
    });

    if (response.error) {
      throw new Error(response.error);
    }

    setBenchmarkPeers(response.rows ?? []);
  };
  const syncBenchmarks = async () => {
    setPeersSyncing(true);
    setPeersError(null);

    try {
      const sync = await apiPost("/organization/benchmarks/sync/", {
        org_id: orgId,
        financial_years: ["2023-24", "2024-25"],
      });

      if (sync.error) {
        throw new Error(sync.details || sync.error);
      }

      const rows = sync.rows ?? [];
      setPeers(rows.map(normalizeBackendBenchmark));

      if (!rows.length) {
        setPeersError("Benchmark sync completed, but no financial rows were available.");
      }
    } catch (error) {
      setPeersError(error instanceof Error ? error.message : "Unable to sync benchmark financials.");
    } finally {
      setPeersSyncing(false);
    }
  };
 
  useEffect(() => {
    if (!hasBenchmarkNewsTarget({ industry: newsIndustry, country: newsCountry })) {
      setNews([]);
      setNewsError(null);
      return;
    }
 
    const controller = new AbortController();
 
    const loadNews = async () => {
      setNewsLoading(true);
      setNewsError(null);
 
      try {
        const results = await fetchIndustryNews({
          industry: newsIndustry,
          country: newsCountry,
          signal: controller.signal,
        });
        setNews(results);
      } catch (error) {
        if (controller.signal.aborted) return;
        setNews([]);
        setNewsError(error instanceof Error ? error.message : "Unable to load live news.");
      } finally {
        if (!controller.signal.aborted) setNewsLoading(false);
      }
    };
 
    loadNews();
    return () => controller.abort();
  }, [newsIndustry, newsCountry]);
 
  const hasMcaScheduleData = peers.some((p) => p.sourceType === "mcaSchedule");
  const chartRows = hasMcaScheduleData
    ? [...peers].sort((a, b) => Math.abs(b.revenue) - Math.abs(a.revenue)).slice(0, 12)
    : peers;
  const chartMetric = hasMcaScheduleData ? "revenue" : "grossMargin";
 
  return (
    <div className="pb-12">
      <PageHeader
        eyebrow="Market Intelligence"
        title="Peer Benchmarks & Benchmark News"
        description="Listed peer comparison and news filtered by organization industry and country."
      />
      <div className="surface-card p-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-primary">
              AI Peer Builder
            </p>
            <h3 className="mt-1 font-display text-lg font-semibold">
              Suggested benchmark companies
            </h3>
          </div>

          <Button type="button" size="sm" disabled={suggestingPeers} onClick={suggestPeers}>
            {suggestingPeers ? "Suggesting..." : "Suggest Peers"}
          </Button>
        </div>

        {peerSuggestions.length > 0 && (
          <div className="divide-y divide-border/60 border border-border rounded-md">
            {peerSuggestions.map((peer) => (
              <label
                key={`${peer.suggested_name}-${peer.company_cin || "na"}`}
                className="grid grid-cols-12 gap-3 px-4 py-3 text-sm items-center"
              >
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    disabled={!peer.verified || !peer.company_cin}
                    checked={Boolean(peer.company_cin && selectedPeerCins[peer.company_cin])}
                    onChange={(e) => {
                      setSelectedPeerCins((prev) => ({
                        ...prev,
                        [peer.company_cin]: e.target.checked,
                      }));
                    }}
                  />
                </div>

                <div className="col-span-4">
                  <p className="font-medium">{peer.company_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {peer.company_cin || "CIN not verified"}
                  </p>
                </div>

                <div className="col-span-2 text-right text-xs">
                  {peer.is_listed ? "Listed" : "Unlisted"}
                </div>

                <div className="col-span-2 text-right text-xs">
                  Score: {peer.score ? Number(peer.score).toFixed(2) : "-"}
                </div>

                <div className="col-span-3 text-xs text-muted-foreground">
                  {peer.reason}
                </div>
              </label>
            ))}
          </div>
        )}

        {peerSuggestions.length > 0 && (
          <div className="flex justify-end">
            <Button type="button" onClick={saveSelectedPeers}>
              Save Selected Peers
            </Button>
          </div>
        )}
      </div>
      <section className="px-6 lg:px-10 mt-6">
        <Tabs defaultValue="benchmarks">
          <TabsList>
            <TabsTrigger value="benchmarks">Peer Benchmarks</TabsTrigger>
            <TabsTrigger value="news">Benchmark News</TabsTrigger>
          </TabsList>
 
          <TabsContent value="benchmarks" className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Showing benchmark rows saved in your database.
              </p>
              <Button type="button" variant="outline" size="sm" disabled={peersSyncing} onClick={syncBenchmarks}>
                {peersSyncing ? "Syncing..." : "Sync Perfios"}
              </Button>
            </div>
 
            {peersError && (
              <div className="surface-card border-warning/30 bg-warning/10 p-4 text-sm text-warning">
                {peersError}
              </div>
            )}
 
            {peersLoading && (
              <div className="surface-card p-6 text-center text-sm text-muted-foreground">
                Loading peer benchmarks from Perfios...
              </div>
            )}
            
            <ChartCard
              title={hasMcaScheduleData ? "MCA Financial Schedule Values" : "Peer Margin vs Energy Intensity"}
              subtitle={hasMcaScheduleData ? "Top schedule line items from Perfios MCA API" : "Bubble proxy via gross margin & kWh / MT"}
              className="h-[320px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartRows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="var(--color-border)" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--color-muted-foreground)" fontSize={10} tickLine={false} axisLine={false} angle={-12} textAnchor="end" height={58} interval={0} />
                  <YAxis
                    stroke="var(--color-muted-foreground)"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => hasMcaScheduleData ? fmtCompact(v) : `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--color-popover)",
                      border: "1px solid var(--color-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(value) => hasMcaScheduleData ? fmt(value) : `${Number(value).toFixed(1)}%`}
                  />
                  <Bar dataKey={chartMetric} name={hasMcaScheduleData ? "Value" : "Gross Margin %"} radius={[6, 6, 0, 0]}>
                    {chartRows.map((p, i) => (
                      <Cell
                        key={i}
                        fill={
                          hasMcaScheduleData
                            ? p.revenue < 0
                              ? "var(--color-destructive)"
                              : "var(--color-primary)"
                            : p.id === "self"
                              ? "var(--color-primary)"
                              : p.rating === "leader"
                                ? "var(--color-success)"
                                : p.rating === "laggard"
                                  ? "var(--color-destructive)"
                                  : "var(--color-chart-2)"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <div className="surface-card overflow-hidden">
              <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border/60 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                <div className="col-span-4">Peer Company</div>
                <div className="col-span-3">CIN</div>
                <div className="col-span-2 text-right">Type</div>
                <div className="col-span-1 text-right">Frequency</div>
                <div className="col-span-2 text-right">Last Sync</div>
              </div>

              <div className="divide-y divide-border/60">
                {benchmarkPeers.map((peer) => (
                  <div key={peer.id} className="grid grid-cols-12 gap-3 px-5 py-3 text-sm">
                    <div className="col-span-4 font-medium truncate">{peer.company_name}</div>
                    <div className="col-span-3 text-xs text-muted-foreground truncate">{peer.company_cin}</div>
                    <div className="col-span-2 text-right text-xs">{peer.is_listed ? "Listed" : "Unlisted"}</div>
                    <div className="col-span-1 text-right text-xs uppercase">{peer.sync_frequency}</div>
                    <div className="col-span-2 text-right text-xs text-muted-foreground">
                      {peer.last_synced_at ? new Date(peer.last_synced_at).toLocaleDateString("en-IN") : "Never"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="surface-card overflow-hidden">
              {hasMcaScheduleData ? (
                <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border/60 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  <div className="col-span-3">Company</div>
                  <div className="col-span-2 text-right">Revenue</div>
                  <div className="col-span-2 text-right">Expenses</div>
                  <div className="col-span-2 text-right">Profit</div>
                  <div className="col-span-1 text-right">Assets</div>
                  <div className="col-span-1 text-right">Equity</div>
                  <div className="col-span-1 text-right">Type</div>
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border/60 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  <div className="col-span-3">Company</div>
                  <div className="col-span-2 text-right">Revenue (TTM)</div>
                  <div className="col-span-1 text-right">YoY</div>
                  <div className="col-span-2 text-right">Gross Margin</div>
                  <div className="col-span-1 text-right">EBITDA</div>
                  <div className="col-span-1 text-right">kWh/MT</div>
                  <div className="col-span-1 text-right">RM Idx</div>
                  <div className="col-span-1 text-right">Rating</div>
                </div>
              )}
              <div className="divide-y divide-border/60">
                {peers.map((p) => {
                  const isSelf = p.id === "self";
                  if (hasMcaScheduleData) {
                    const m = p.metrics;
                    return (
                      <div key={p.id} className="px-5 py-4 text-sm transition-colors hover:bg-muted/40">
                        <div className="grid grid-cols-12 gap-3 items-center">
                          <div className="col-span-3 min-w-0">
                            <p className="font-medium truncate">{p.name}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{p.ticker} · {m?.period_end ?? "Latest"}</p>
                          </div>
                          <MetricValue className="col-span-2" value={getMcaIncome(m)} />
                          <MetricValue className="col-span-2" value={getMcaExpenses(m)} />
                          <MetricValue className="col-span-2" value={getMcaProfitAfterTax(m)} />
                          <MetricValue className="col-span-1" value={getMcaTotalAssets(m)} />
                          <MetricValue className="col-span-1" value={getMcaTotalEquity(m)} />
                          <div className="col-span-1 text-right">
                            <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">
                              MCA
                            </span>
                          </div>
                        </div>
                        <MetricChipGrid metrics={m} />
                      </div>
                    );
                  }
 
                  return (
                    <div
                      key={p.id}
                      className={`grid grid-cols-12 gap-3 px-5 py-3 items-center text-sm transition-colors ${
                        isSelf ? "bg-primary/5" : "hover:bg-muted/40"
                      }`}
                    >
                      <div className="col-span-3">
                        <p className={`font-medium ${isSelf ? "text-primary" : ""}`}>{p.name}</p>
                        <p className="text-[10px] text-muted-foreground">{p.ticker} · {p.segment}</p>
                      </div>
                      <div className="col-span-2 text-right font-mono text-xs">{fmt(p.revenue)}</div>
                      <div className={`col-span-1 text-right font-mono text-xs ${p.growthYoY >= 0 ? "text-success" : "text-destructive"}`}>
                        {p.growthYoY >= 0 ? "+" : ""}{p.growthYoY.toFixed(1)}%
                      </div>
                      <div className="col-span-2 text-right font-mono text-xs">{p.grossMargin.toFixed(1)}%</div>
                      <div className="col-span-1 text-right font-mono text-xs">{p.ebitdaMargin.toFixed(1)}%</div>
                      <div className="col-span-1 text-right font-mono text-xs">{p.energyIntensity}</div>
                      <div className="col-span-1 text-right font-mono text-xs">{p.rmCostIndex}</div>
                      <div className="col-span-1 flex justify-end">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
                            p.rating === "leader"
                              ? "border-success/30 bg-success/10 text-success"
                              : p.rating === "laggard"
                                ? "border-destructive/30 bg-destructive/10 text-destructive"
                                : "border-border bg-muted text-muted-foreground"
                          }`}
                        >
                          {p.rating}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
 
            {!peersLoading && !peersError && peers.length === 0 && (
              <div className="surface-card p-6 text-center text-sm text-muted-foreground">
                No Perfios peer benchmark data available.
              </div>
            )}
          </TabsContent>
 
          <TabsContent value="news" className="mt-4 space-y-4">
            <div className="surface-card p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-primary">
                    Benchmark News
                  </p>
                  <h3 className="mt-1 font-display text-lg font-semibold">Industry and country</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" disabled={newsSyncing || !hasBenchmarkNewsTarget({ industry: newsIndustry, country: newsCountry })} onClick={syncIndustryNews}>
                    {newsSyncing ? "Syncing..." : "Sync News"}
                  </Button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
                  <span className="text-xs font-medium text-muted-foreground">Industry</span>
                  <p className="mt-1 text-sm font-semibold text-foreground">
                    {newsIndustry || "Not found"}
                  </p>
                </div>
                <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
                  <span className="text-xs font-medium text-muted-foreground">Country</span>
                  <p className="mt-1 text-sm font-semibold text-foreground">
                    {newsCountry || "Not found"}
                  </p>
                </div>
              </div>
            </div>
 
            {newsError && (
              <div className="surface-card border-warning/30 bg-warning/10 p-4 text-sm text-warning">
                {newsError}
              </div>
            )}
 
            {newsLoading && (
              <div className="surface-card p-6 text-center text-sm text-muted-foreground">
                Loading saved benchmark news...
              </div>
            )}
 
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {news.map((n) => {
                const sentClass =
                  n.sentiment === "positive"
                    ? "border-success/30 bg-success/10 text-success"
                    : n.sentiment === "negative"
                      ? "border-destructive/30 bg-destructive/10 text-destructive"
                      : "border-border bg-muted text-muted-foreground";
                const impactClass =
                  n.impact === "high"
                    ? "text-destructive"
                    : n.impact === "medium"
                      ? "text-warning"
                      : "text-muted-foreground";
                return (
                  <article
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedNews(n)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedNews(n);
                      }
                    }}
                    className="surface-card p-5 hover:border-primary/40 hover:shadow-md transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      <span>{n.source}</span>
                      <span>·</span>
                      <span>{formatNewsPublishedAt(n)}</span>
                      <span>·</span>
                      <span>{n.region}</span>
                      <span className="ml-auto inline-flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${sentClass}`}>
                          {n.sentiment}
                        </span>
                        <span className={`text-[10px] font-semibold ${impactClass}`}>
                          {n.impact.toUpperCase()} IMPACT
                        </span>
                      </span>
                    </div>
                    <h4 className="mt-2 text-sm font-semibold leading-snug">{n.headline}</h4>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{n.summary}</p>
                    <p className="mt-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                      {n.sector} · {n.category}
                    </p>
                    <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                      <span className="text-xs font-medium text-primary">View summary</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          window.open(n.url, "_blank", "noopener,noreferrer");
                        }}
                      >
                        Read original
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </article>
                );
              })}
            </div>
            {!newsLoading && !newsError && news.length === 0 && (
              <div className="surface-card p-6 text-center text-sm text-muted-foreground">
                Add organization industry and address to show benchmark news.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>
 
      <Dialog open={Boolean(selectedNews)} onOpenChange={(open) => !open && setSelectedNews(null)}>
        {selectedNews && (
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                <span>{selectedNews.source}</span>
                <span>/</span>
                <span>{formatNewsPublishedAt(selectedNews)}</span>
                <span>/</span>
                <span>{selectedNews.region}</span>
              </div>
              <DialogTitle className="mt-3 text-xl leading-snug">{selectedNews.headline}</DialogTitle>
              <DialogDescription className="flex flex-wrap gap-2 pt-2 uppercase tracking-[0.14em]">
                <span>{selectedNews.sector}</span>
                <span>/</span>
                <span>{selectedNews.category}</span>
                <span>/</span>
                <span>{selectedNews.sentiment} sentiment</span>
                <span>/</span>
                <span>{selectedNews.impact} impact</span>
              </DialogDescription>
            </DialogHeader>
            <div className="border-t border-border pt-5">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-primary">AI Summary</p>
              <p className="mt-3 text-base leading-7 text-foreground">{selectedNews.summary}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSelectedNews(null)}>
                Close
              </Button>
              <Button asChild>
                <a href={selectedNews.url} target="_blank" rel="noopener noreferrer">
                  Read original
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
 
async function fetchPerfiosBenchmarks(orgId, signal) {
  
  if (signal.aborted) return [];
 
  const response = await apiGet(`/organization/benchmarks/${orgId ? `?org_id=${encodeURIComponent(orgId)}` : ""}`);
  const rows = response.rows ?? [];
 
  return rows.map(normalizeBackendBenchmark);
}
 

function safeJsonParse(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
 
function getOrganizationNewsProfile(account) {
  const candidates = getOrganizationCandidates(account);
  const industry = candidates.map((org) => cleanOrgText(org?.industry)).find(Boolean) || "";
  const country =
    candidates
      .map((org) => detectCountryFromAddress(org?.address))
      .find(Boolean) || "";
 
  return {
    industry,
    country,
  };
}
 
function getOrganizationCandidates(account) {
  const storageValues = getStoredOrganizationValues();
  const accountUser = account?.user || {};
  const accountOrgMember = account?.org_member || {};
 
  return [
    account?.org,
    account?.org?.data,
    account?.org_data,
    account?.org_data?.data,
    account?.organization,
    account?.organization?.data,
    accountUser?.org,
    accountUser?.org?.data,
    accountUser?.org_data,
    accountUser?.org_data?.data,
    accountUser?.organization,
    accountUser?.organization?.data,
    accountOrgMember?.org,
    accountOrgMember?.org?.data,
    accountOrgMember?.org_data,
    accountOrgMember?.org_data?.data,
    ...storageValues,
  ].filter((value) => value && typeof value === "object");
}
 
function getStoredOrganizationValues() {
  if (typeof window === "undefined") return [];
 
  const directKeys = ["org", "org_data", "organization", "account", "berry-account", "user", "user_data"];
  const directValues = directKeys.flatMap((key) => expandStoredObject(safeJsonParse(window.localStorage.getItem(key))));
  const persistedRoot = safeJsonParse(window.localStorage.getItem("persist:root"));
  const persistedAccount = parseMaybeJson(persistedRoot?.account);
 
  return [
    ...directValues,
    ...expandStoredObject(persistedAccount),
  ];
}
 
function expandStoredObject(value) {
  const parsed = parseMaybeJson(value);
  if (!parsed || typeof parsed !== "object") return [];
 
  return [
    parsed,
    parsed.data,
    parsed.org,
    parsed.org?.data,
    parsed.org_data,
    parsed.org_data?.data,
    parsed.organization,
    parsed.organization?.data,
    parsed.user?.org,
    parsed.user?.org?.data,
    parsed.user?.org_data,
    parsed.user?.org_data?.data,
    parsed.UserDetails?.org,
    parsed.UserDetails?.org?.data,
    parsed.UserDetails?.org_data,
    parsed.UserDetails?.org_data?.data,
  ].filter((item) => item && typeof item === "object");
}
 
function parseMaybeJson(value) {
  if (typeof value !== "string") return value;
  return safeJsonParse(value) || value;
}
 
function cleanOrgText(value) {
  if (typeof value !== "string") return "";
  const text = value.trim();
  if (!text) return "";
 
  const normalized = text.toUpperCase();
  if (normalized === "NA" || normalized === "N/A" || normalized === "NULL" || normalized === "NOT FOUND") {
    return "";
  }
 
  return text;
}
 
function detectCountryFromAddress(address) {
  if (typeof address !== "string") return "";
  const text = address.toLowerCase();
 
  if (/\bindia\b/.test(text) || /\b(pune|mumbai|mizoram|maharashtra|gujarat|delhi|karnataka|tamil nadu|telangana|rajasthan|uttar pradesh|west bengal)\b/.test(text)) {
    return "India";
  }
 
  const countries = [
    "United Arab Emirates",
    "UAE",
    "Saudi Arabia",
    "Qatar",
    "Oman",
    "Kuwait",
    "Bahrain",
    "United States",
    "USA",
    "United Kingdom",
    "UK",
    "Singapore",
    "Germany",
    "France",
    "China",
    "Japan",
    "Australia",
    "South Africa",
  ];
 
  const found = countries.find((country) => text.includes(country.toLowerCase()));
  if (!found) return "";
  if (found === "UAE") return "United Arab Emirates";
  if (found === "USA") return "United States";
  if (found === "UK") return "United Kingdom";
  return found;
}
 
function normalizeBackendBenchmark(row) {
  const grossMargin = toNumber(row.gross_profit_margin);

  const revenue = firstNonZeroNumber(
    row.total_revenue,
    row.total_assets,
    row.total_equity,
    row.profit_before_tax,
  );

  return {
    id: String(row.id),
    name: row.company_name || row.company_cin || "Benchmark Company",
    ticker: row.company_cin || "-",
    segment: row.industry_code || "Benchmark",

    isListed: Boolean(row.is_listed),
    syncFrequency: row.is_listed ? "quarterly" : "annual",
    financialYear: row.financial_year || "-",
    periodType: row.period_type || "ANNUAL",
    periodEnd: row.period_end || "-",

    sourceType: "mcaSchedule",

    revenue,
    totalExpenses: toNumber(row.total_expenses),
    profitBeforeTax: toNumber(row.profit_before_tax),
    profitAfterTax: toNumber(row.profit_after_tax),
    totalAssets: toNumber(row.total_assets),
    totalEquity: toNumber(row.total_equity),

    roa: toNumber(row.return_on_assets),
    roe: toNumber(row.return_on_equity),
    equityMultiplier: toNumber(row.equity_multiplier),

    growthYoY: toNumber(row.revenue_growth),
    grossMargin: toNumber(row.gross_profit_margin),
    ebitdaMargin: toNumber(row.ebitda_margin),
    energyIntensity: toNumber(row.current_ratio),
    rmCostIndex: toNumber(row.debt_equity_ratio),

    rating: normalizeRating(toNumber(row.gross_profit_margin)),
    metrics: row,
  };
}

function MetricValue({ className, value }) {
  if (value === null || value === undefined || value === "") {
    return (
      <div className={`${className ?? ""} text-right text-xs text-muted-foreground`}>
        Not available
      </div>
    );
  }
 
  const number = toNumber(value);
  return (
    <div className={`${className ?? ""} text-right font-mono text-xs`}>
      {fmtInrCompact(number)}
    </div>
  );
}
 
function getMcaIncome(metrics) {
  const profit = toNumber(getMcaProfitBeforeTax(metrics));
  const candidates = [
    metrics?.revenue_from_operations,
    metrics?.revenueFromOperations,
    metrics?.revenue_from_sale_of_products,
    metrics?.revenueFromSaleOfProducts,
    metrics?.revenue_from_sale_of_goods,
    metrics?.revenueFromSaleOfGoods,
    metrics?.income_from_operations,
    metrics?.incomeFromOperations,
    metrics?.total_income,
    metrics?.totalIncome,
    metrics?.gross_revenue,
    metrics?.grossRevenue,
    metrics?.turnover,
    metrics?.sales,
    metrics?.income,
    metrics?.total_revenue,
  ];
 
  for (const candidate of candidates) {
    const value = toNumber(candidate);
    if (value > 0 && (!profit || value >= profit)) return value;
  }
 
  return null;
}
 
function getMcaExpenses(metrics) {
  const expenses = toNumber(metrics?.total_expenses);
  const profit = toNumber(getMcaProfitBeforeTax(metrics));
 
  if (expenses > 0 && (!profit || expenses >= profit)) return expenses;
  return null;
}
 
function getMcaProfitBeforeTax(metrics) {
  return firstNonZeroNumber(
    metrics?.profit_before_tax,
    metrics?.profitLossForPeriod,
    metrics?.schChangesInEquity?.profitLossForPeriod,
  ) || null;
}
 
function getMcaProfitAfterTax(metrics) {
  return firstNonZeroNumber(
    metrics?.profit_after_tax,
    metrics?.profitLossForPeriod,
    metrics?.schChangesInEquity?.profitLossForPeriod,
    metrics?.profit_before_tax,
  ) || null;
}
 
function getMcaTotalAssets(metrics) {
  return firstNonZeroNumber(
    metrics?.total_assets,
    sumLooseNumbers(
      metrics?.schPropertyPlantAndEquipment?.propertyPlantAndEquipmentAtEndOfPeriod,
      investmentScheduleTotal(metrics?.schCurrentInvestment),
      investmentScheduleTotal(metrics?.schNoncurrentInvestment),
    ),
  ) || null;
}
 
function getMcaTotalEquity(metrics) {
  return firstNonZeroNumber(
    metrics?.total_equity,
    metrics?.schChangesInEquity?.balanceAtEndOfPeriod,
    metrics?.schEquityShareCapital?.capitalAtTheEndOfPeriod,
  ) || null;
}
 
function getMcaTotalAssetsGrowth(metrics) {
  const direct = toNumber(metrics?.total_assets_growth);
  if (direct) return direct;
 
  return percentFromValues(
    metrics?.schPropertyPlantAndEquipment?.totalIncreaseDecreaseInPropertyPlantEquipment,
    metrics?.schPropertyPlantAndEquipment?.propertyPlantAndEquipmentAtBeginningOfPeriod,
  );
}
 
function MetricChip({ label, value, suffix = "" }) {
  return (
    <div className="rounded-md border border-border/70 bg-muted/20 px-2.5 py-2">
      <p className="truncate text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-xs">
        {value}
        {suffix}
      </p>
    </div>
  );
}
 
function MetricChipGrid({ metrics }) {
  const chips = buildMcaSummaryChips(metrics);
 
  if (!chips.length) return null;
 
  return (
    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
      {chips.map((chip) => (
        <MetricChip
          key={chip.label}
          label={chip.label}
          value={chip.value}
          suffix={chip.suffix}
        />
      ))}
    </div>
  );
}
 
function buildMcaSummaryChips(metrics) {
  const totalAssets = toNumber(getMcaTotalAssets(metrics));
  const totalEquity = toNumber(getMcaTotalEquity(metrics));
  const profitAfterTax = toNumber(getMcaProfitAfterTax(metrics));
  const roa = totalAssets ? (profitAfterTax / totalAssets) * 100 : toNumber(metrics?.return_on_assets);
  const roe = totalEquity ? (profitAfterTax / totalEquity) * 100 : toNumber(metrics?.return_on_equity);
 
  const chips = [
    finitePercentChip("ROA", roa),
    finitePercentChip("ROE", roe),
    positiveNumberChip("Equity Mult.", totalEquity ? totalAssets / totalEquity : metrics?.equity_multiplier),
    positivePercentChip("Assets Growth", getMcaTotalAssetsGrowth(metrics)),
  ];
 
  return chips.filter(Boolean);
}
 
function finitePercentChip(label, value) {
  const number = toNumber(value);
  if (!Number.isFinite(number) || Math.abs(number) > 200 || number === 0) return null;
  return { label, value: number.toLocaleString("en-IN", { maximumFractionDigits: 2 }), suffix: "%" };
}
 
function positivePercentChip(label, value) {
  const number = toNumber(value);
  if (!Number.isFinite(number) || number === 0 || Math.abs(number) > 200) return null;
  return { label, value: number.toLocaleString("en-IN", { maximumFractionDigits: 2 }), suffix: "%" };
}
 
function positiveNumberChip(label, value) {
  const number = toNumber(value);
  if (!Number.isFinite(number) || number <= 0 || number > 100) return null;
  return { label, value: number.toLocaleString("en-IN", { maximumFractionDigits: 2 }) };
}
 
function isMcaScheduleRow(row) {
  const hasScheduleValues = row.total_assets != null || row.total_equity != null || row.profit_before_tax != null;
  const hasNoRealBenchmarkRatios =
    toNumber(row.current_ratio) === 0 &&
    toNumber(row.quick_ratio) === 0 &&
    toNumber(row.debt_equity_ratio) === 0 &&
    toNumber(row.inventory_turnover) === 0 &&
    toNumber(row.interest_coverage) === 0;
 
  return hasScheduleValues && !row.industry_code && hasNoRealBenchmarkRatios;
}
 
function normalizeRating(grossMargin) {
  if (grossMargin >= 37) return "leader";
  if (grossMargin < 32) return "laggard";
  return "peer";
}
 
function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[$,%\s,]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}
 
function firstNonZeroNumber(...values) {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== 0) return parsed;
  }
 
  return 0;
}
 
function sumLooseNumbers(...values) {
  let total = 0;
  let found = false;
  values.forEach((value) => {
    const parsed = toNumber(value);
    if (parsed) {
      total += parsed;
      found = true;
    }
  });
  return found ? total : null;
}
 
function investmentScheduleTotal(items) {
  if (!Array.isArray(items)) return null;
  return sumLooseNumbers(...items.map((item) => item?.investmentINR));
}
 
function percentFromValues(numerator, denominator) {
  const top = toNumber(numerator);
  const bottom = toNumber(denominator);
  if (!top || !bottom) return null;
  return (top / bottom) * 100;
}
 
function hasBenchmarkNewsTarget(config) {
  return Boolean(
    config?.industry?.trim() &&
    config?.country?.trim()
  );
}
 
async function fetchIndustryNews({ industry, country, signal }) {
  const normalizedIndustry = industry.trim();
  const normalizedCountry = country.trim();
  const params = new URLSearchParams({
    sectors: normalizedIndustry,
    regions: normalizedCountry,
    industry: normalizedIndustry,
    country: normalizedCountry,
    limit: "24",
  });
 
  if (signal.aborted) return [];
  const response = await apiGet(`/user/industry-news/?${params.toString()}`);
  if (signal.aborted) return [];
  const storedItems = sortTimestampedNews(response.items ?? []);
  if (storedItems.length > 0) return storedItems;
 
  const synced = await apiPost("/user/industry-news/", {
    sectors: [normalizedIndustry],
    regions: [normalizedCountry],
    industry: normalizedIndustry,
    country: normalizedCountry,
    limit: 24,
  });
  if (signal.aborted) return [];
  return sortTimestampedNews(synced.items ?? []);
}
 
function sortTimestampedNews(items) {
  return items
    .filter((item) => {
      if (!item?.publishedAt || typeof item.publishedAt !== "string" || !/\b\d{4}\b/.test(item.publishedAt)) return false;
      return !Number.isNaN(new Date(item.publishedAt).getTime());
    })
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
}
 
function formatNewsPublishedAt(newsItem) {
  if (!newsItem?.publishedAt) return "Date unavailable";
  if (typeof newsItem.publishedAt === "string" && !/\b\d{4}\b/.test(newsItem.publishedAt)) {
    return "Date unavailable";
  }
  const published = new Date(newsItem.publishedAt);
  if (Number.isNaN(published.getTime())) return "Date unavailable";
  return published.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
